import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	BadgeEuro,
	CalendarClock,
	Check,
	Copy,
	MousePointerClick,
	Share2,
	Sparkles,
	Users,
} from "lucide-react";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { Reveal } from "@/components/reveal";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";
import { cn } from "@/lib/utils";

const AFFILIATE_STEPS = [
	{
		icon: Share2,
		title: "Share your link",
		description:
			"Every account gets a personal referral link from the billing page.",
	},
	{
		icon: MousePointerClick,
		title: "A new user signs up",
		description:
			"AgentReacher records the first referral when someone creates an account.",
	},
	{
		icon: BadgeEuro,
		title: "Earn on paid plans",
		description:
			"Receive 30% of referred subscription revenue for the first 12 months.",
	},
];

const FITS = [
	"AI builders with audiences",
	"Social media consultants",
	"Creator tool reviewers",
	"Agencies and marketers",
	"Newsletter writers",
	"MCP and automation educators",
];

const FAQS = [
	{
		question: "Who can join?",
		answer:
			"Any AgentReacher user can share their affiliate link. Create an account, open Billing, and copy your link.",
	},
	{
		question: "What is the commission?",
		answer:
			"Affiliates earn 30% of attributed subscription revenue for 12 months after a referred user starts paying.",
	},
	{
		question: "Does the referred customer get a discount?",
		answer:
			"No. This is a referral commission for the user who shared AgentReacher, not a Stripe promotion code.",
	},
];

export const Route = createFileRoute("/affiliate-program")({
	component: AffiliateProgramPage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({
			matches,
			internalPath: "/affiliate-program",
		});

		return {
			links: buildLocalizedSeoLinks({
				matches,
				internalPath: "/affiliate-program",
			}),
			meta: [
				{ title: "AgentReacher Affiliate Program | Earn 30% for 12 months" },
				{
					name: "description",
					content:
						"Share AgentReacher with AI builders, agencies, and marketers. Earn 30% of referred subscription revenue for 12 months.",
				},
				{
					property: "og:title",
					content: "AgentReacher Affiliate Program",
				},
				{
					property: "og:description",
					content:
						"Earn 30% for 12 months when referred users become paying AgentReacher customers.",
				},
				{ property: "og:image", content: ogImageUrl("default") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("default") },
			],
		};
	},
});

function AffiliateProgramPage() {
	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />

			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						<section className="relative overflow-hidden rounded-md bg-white dark:bg-black">
							<div className="grid gap-px bg-border lg:grid-cols-[1fr_0.82fr]">
								<div className="homepage-hover-cell flex min-h-[33rem] flex-col justify-center rounded-md bg-white dark:bg-black px-6 py-12 sm:px-10 lg:px-12">
									<Reveal>
										<div className="inline-flex w-fit items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground">
											<Sparkles className="size-3.5 text-blue-600" />
											Affiliate program
										</div>
										<h1 className="mt-6 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-5xl lg:text-6xl">
											Earn 30% when you refer AgentReacher.
										</h1>
										<p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
											Share the social scheduling tool built for AI agents. When
											someone signs up through your link and becomes a paying
											customer, you earn 30% for 12 months.
										</p>
										<div className="mt-8 flex flex-col gap-3 sm:flex-row">
											<Link
												to="/signup"
												className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
											>
												Start sharing
												<ArrowRight className="size-4" />
											</Link>
											<Link
												to="/dashboard/billing"
												className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted/60"
											>
												Copy my link
											</Link>
										</div>
									</Reveal>
								</div>
								<div className="homepage-hover-cell rounded-md bg-white dark:bg-black p-5 sm:p-8 lg:p-10">
									<AffiliateVisual />
								</div>
							</div>
						</section>

						<section className="grid gap-px bg-border md:grid-cols-3">
							<StatBlock label="Commission" value="30%" />
							<StatBlock label="Earning window" value="12 months" />
							<StatBlock label="Setup" value="1 link" />
						</section>

						<section className="rounded-md bg-white dark:bg-black px-6 py-12 sm:px-10 sm:py-14">
							<div className="mx-auto max-w-2xl text-center">
								<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
									How it works
								</p>
								<h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em] sm:text-3xl">
									Simple enough to explain in one message.
								</h2>
							</div>
							<div className="mt-10 grid gap-px overflow-hidden rounded-md bg-border md:grid-cols-3">
								{AFFILIATE_STEPS.map((step, index) => (
									<div
										key={step.title}
										className="homepage-hover-cell rounded-md bg-white dark:bg-black p-6"
									>
										<div className="flex items-center justify-between gap-4">
											<span className="flex size-10 items-center justify-center rounded-md border border-border bg-muted/40">
												<step.icon className="size-5 text-blue-600" />
											</span>
											<span className="font-mono text-xs text-muted-foreground">
												0{index + 1}
											</span>
										</div>
										<h3 className="mt-6 text-lg font-semibold tracking-tight">
											{step.title}
										</h3>
										<p className="mt-2 text-sm leading-6 text-muted-foreground">
											{step.description}
										</p>
									</div>
								))}
							</div>
						</section>

						<section className="grid gap-px bg-border lg:grid-cols-[0.85fr_1fr]">
							<div className="homepage-hover-cell rounded-md bg-white dark:bg-black px-6 py-10 sm:px-10">
								<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
									Best fit
								</p>
								<h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em]">
									For people who already explain AI workflows.
								</h2>
								<p className="mt-3 text-sm leading-6 text-muted-foreground">
									If your audience cares about content systems, social
									distribution, AI agents, MCP, or marketing automation, this
									program gives you a clean product to recommend.
								</p>
							</div>
							<div className="grid gap-px bg-border sm:grid-cols-2">
								{FITS.map((fit) => (
									<div
										key={fit}
										className="homepage-hover-cell flex items-center gap-3 rounded-md bg-white dark:bg-black p-5 text-sm font-medium"
									>
										<Check className="size-4 shrink-0 text-blue-600" />
										<span>{fit}</span>
									</div>
								))}
							</div>
						</section>

						<section className="rounded-md bg-white dark:bg-black px-6 py-12 sm:px-10 sm:py-14">
							<div className="grid gap-8 lg:grid-cols-[0.8fr_1fr] lg:items-start">
								<div>
									<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
										Details
									</p>
									<h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em]">
										No coupon codes. No special checkout links.
									</h2>
									<p className="mt-3 text-sm leading-6 text-muted-foreground">
										The link attributes the signup to you. Commission terms are
										stored with the referred subscription, so revenue can be
										reconciled without changing the customer price.
									</p>
								</div>
								<div className="grid gap-px overflow-hidden rounded-md bg-border">
									{FAQS.map((faq) => (
										<div
											key={faq.question}
											className="rounded-md bg-white dark:bg-black p-5"
										>
											<h3 className="text-sm font-semibold text-foreground">
												{faq.question}
											</h3>
											<p className="mt-2 text-sm leading-6 text-muted-foreground">
												{faq.answer}
											</p>
										</div>
									))}
								</div>
							</div>
						</section>

						<section className="homepage-hover-cell rounded-md bg-white dark:bg-black px-6 py-12 text-center sm:px-10 sm:py-14">
							<h2 className="text-2xl font-semibold tracking-[-0.03em] sm:text-3xl">
								Turn your recommendation into recurring revenue.
							</h2>
							<p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-muted-foreground sm:text-base">
								Create an account, copy your affiliate link, and share
								AgentReacher with the marketers who need AI-native social
								scheduling.
							</p>
							<div className="mt-7 flex justify-center">
								<Link
									to="/signup"
									className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
								>
									Get your affiliate link
									<ArrowRight className="size-4" />
								</Link>
							</div>
						</section>
					</div>
				</div>
			</main>

			<PublicFooter />
		</div>
	);
}

function StatBlock({ label, value }: { label: string; value: string }) {
	return (
		<div className="homepage-hover-cell rounded-md bg-white dark:bg-black p-6 text-center">
			<p className="text-sm text-muted-foreground">{label}</p>
			<p className="mt-2 text-3xl font-semibold tracking-[-0.04em] text-foreground">
				{value}
			</p>
		</div>
	);
}

function AffiliateVisual() {
	return (
		<div className="flex h-full min-h-[27rem] flex-col justify-between rounded-md border border-border bg-muted/20 p-4">
			<div className="rounded-md border border-border bg-background p-4">
				<div className="flex items-center justify-between gap-3">
					<div>
						<p className="text-xs text-muted-foreground">Your affiliate link</p>
						<p className="mt-2 font-mono text-sm text-foreground">
							agentreacher.com/signup?ref=k7m2q9t1ab
						</p>
					</div>
					<span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
						<Copy className="size-4" />
					</span>
				</div>
			</div>

			<div className="my-6 grid gap-3">
				<FlowRow
					icon={Users}
					label="3 referred signups"
					className="ml-0 max-w-[82%]"
				/>
				<FlowRow
					icon={CalendarClock}
					label="2 active paid plans"
					className="ml-auto max-w-[84%]"
				/>
				<FlowRow
					icon={BadgeEuro}
					label="30% commission tracked"
					className="ml-6 max-w-[88%]"
				/>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="rounded-md border border-border bg-background p-4">
					<p className="text-xs text-muted-foreground">Commission</p>
					<p className="mt-1 text-2xl font-semibold tracking-[-0.04em]">30%</p>
				</div>
				<div className="rounded-md border border-border bg-background p-4">
					<p className="text-xs text-muted-foreground">Term</p>
					<p className="mt-1 text-2xl font-semibold tracking-[-0.04em]">
						12 mo
					</p>
				</div>
			</div>
		</div>
	);
}

function FlowRow({
	icon: Icon,
	label,
	className,
}: {
	icon: typeof Users;
	label: string;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex items-center gap-3 rounded-md border border-border bg-background p-3 text-sm shadow-sm",
				className,
			)}
		>
			<span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-blue-600/10 text-blue-600">
				<Icon className="size-4" />
			</span>
			<span className="font-medium text-foreground">{label}</span>
		</div>
	);
}
